import React, { useState } from 'react';
import { Cpu, TrendingDown, Zap, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppSwitch } from '../components/base/AppSwitch';
import { AppBadge } from '../components/base/AppBadge';
import { Toast } from '../components/Toast';
interface AIModel {
  id: string;
  name: string;
  provider: string;
  enabled: boolean;
  usagePercentage: number;
  costPerRequest: number;
  avgResponseTime: number;
  totalRequests: number;
  description: string;
}
export function ModelsPage() {
  const [isAutoMode, setIsAutoMode] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [models, setModels] = useState<AIModel[]>([{
    id: 'm1',
    name: 'GPT-4 Turbo',
    provider: 'OpenAI',
    enabled: true,
    usagePercentage: 35,
    costPerRequest: 0.03,
    avgResponseTime: 2.1,
    totalRequests: 1250,
    description: 'Modello avanzato per task complessi e ragionamento profondo'
  }, {
    id: 'm2',
    name: 'GPT-3.5 Turbo',
    provider: 'OpenAI',
    enabled: true,
    usagePercentage: 45,
    costPerRequest: 0.002,
    avgResponseTime: 0.8,
    totalRequests: 1800,
    description: 'Modello veloce ed economico per task standard'
  }, {
    id: 'm3',
    name: 'Claude 3 Opus',
    provider: 'Anthropic',
    enabled: true,
    usagePercentage: 15,
    costPerRequest: 0.015,
    avgResponseTime: 1.5,
    totalRequests: 600,
    description: 'Eccellente per analisi e comprensione contestuale'
  }, {
    id: 'm4',
    name: 'Claude 3 Sonnet',
    provider: 'Anthropic',
    enabled: false,
    usagePercentage: 5,
    costPerRequest: 0.003,
    avgResponseTime: 1.2,
    totalRequests: 200,
    description: 'Bilanciamento ottimale tra performance e costi'
  }, {
    id: 'm5',
    name: 'Gemini Pro',
    provider: 'Google',
    enabled: false,
    usagePercentage: 0,
    costPerRequest: 0.00025,
    avgResponseTime: 0.9,
    totalRequests: 0,
    description: 'Modello multimodale con costi contenuti'
  }]);
  const handleToggleModel = (modelId: string) => {
    if (isAutoMode) {
      setToast({
        message: 'Disattiva la modalità automatica per gestire manualmente i modelli',
        type: 'info'
      });
      return;
    }
    setModels(prev => prev.map(m => m.id === modelId ? {
      ...m,
      enabled: !m.enabled
    } : m));
    const model = models.find(m => m.id === modelId);
    setToast({
      message: `${model?.name} ${model?.enabled ? 'disattivato' : 'attivato'}`,
      type: 'success'
    });
  };
  const handleToggleAutoMode = () => {
    setIsAutoMode(!isAutoMode);
    setToast({
      message: !isAutoMode ? 'Modalità automatica attivata - Il Service ottimizzerà la selezione dei modelli' : 'Modalità manuale attivata - Puoi gestire i modelli individualmente',
      type: 'success'
    });
  };
  const activeModels = models.filter(m => m.enabled).length;
  const totalRequests = models.reduce((sum, m) => sum + m.totalRequests, 0);
  const avgCostPerRequest = models.reduce((sum, m) => sum + m.costPerRequest * m.totalRequests, 0) / totalRequests;
  const costSavings = 28; // Percentage saved compared to using only premium models
  return <div className="pb-12 space-y-6" data-id="element-1529">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1530" />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1531">
        <div data-id="element-1532">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-1533">
            Models
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1534">
            Gestisci i modelli AI e ottimizza costi ed efficienza
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1535">
        <AppCard data-id="element-1536">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1537">
            {costSavings}%
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1538">
            Risparmio costi
          </p>
        </AppCard>

        <AppCard data-id="element-1539">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1540">
            1.2s
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1541">
            Tempo medio risposta
          </p>
        </AppCard>

        <AppCard data-id="element-1542">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1543">
            {activeModels}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1544">
            Modelli attivi
          </p>
        </AppCard>

        <AppCard data-id="element-1545">
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1546">
            {totalRequests.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1547">
            Richieste totali
          </p>
        </AppCard>
      </div>

      {/* Automatic Mode Toggle */}
      <AppCard data-id="element-1548">
        <div className="flex items-center justify-between" data-id="element-1549">
          <div className="flex items-start gap-3" data-id="element-1550">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-1551">
              <Cpu className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1552" />
            </div>
            <div data-id="element-1553">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100" data-id="element-1554">
                Gestione automatica modelli
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-1555">
                Il Service seleziona automaticamente il modello ottimale per
                ogni richiesta, bilanciando costi, velocità e qualità della
                risposta
              </p>
            </div>
          </div>
          <AppSwitch checked={isAutoMode} onChange={handleToggleAutoMode} data-id="element-1556" />
        </div>
      </AppCard>

      {/* Info Banner - Only show in auto mode */}
      {isAutoMode && <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl" data-id="element-1557">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-1558" />
          <div data-id="element-1559">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100" data-id="element-1560">
              Ottimizzazione intelligente attiva
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1" data-id="element-1561">
              Il Service analizza ogni richiesta e seleziona il modello più
              appropriato in base a complessità, contesto e requisiti di
              performance. Questo garantisce il miglior rapporto qualità-prezzo.
            </p>
          </div>
        </div>}

      {/* Models List */}
      <div className="space-y-4" data-id="element-1562">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1563">
          Modelli disponibili
        </h2>

        {models.map(model => <AppCard key={model.id} className="hover:shadow-md transition-shadow" data-id="element-1564">
            <div className="space-y-4" data-id="element-1565">
              {/* Model Header */}
              <div className="flex items-start justify-between" data-id="element-1566">
                <div className="flex items-start gap-4 flex-1" data-id="element-1567">
                  <div className={`p-2.5 rounded-xl ${model.enabled ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-100 dark:bg-gray-800'}`} data-id="element-1568">
                    <Cpu className={`w-5 h-5 ${model.enabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-600'}`} data-id="element-1569" />
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1570">
                    <div className="flex items-center gap-3 mb-1" data-id="element-1571">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100" data-id="element-1572">
                        {model.name}
                      </h3>
                      <AppBadge variant="gray" data-id="element-1573">{model.provider}</AppBadge>
                      {model.enabled && <AppBadge variant="green" data-id="element-1574">Attivo</AppBadge>}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1575">
                      {model.description}
                    </p>
                  </div>
                </div>

                <AppSwitch checked={model.enabled} onChange={() => handleToggleModel(model.id)} disabled={isAutoMode} data-id="element-1576" />
              </div>

              {/* Model Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100 dark:border-dark-subtle" data-id="element-1577">
                <div data-id="element-1578">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1579">
                    Utilizzo
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1580">
                    {model.usagePercentage}%
                  </p>
                </div>
                <div data-id="element-1581">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1582">
                    Costo/richiesta
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1583">
                    €{model.costPerRequest.toFixed(3)}
                  </p>
                </div>
                <div data-id="element-1584">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1585">
                    Tempo medio
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1586">
                    {model.avgResponseTime}s
                  </p>
                </div>
                <div data-id="element-1587">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1588">
                    Richieste
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1589">
                    {model.totalRequests.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Usage Bar */}
              {model.enabled && model.usagePercentage > 0 && <div data-id="element-1590">
                  <div className="flex items-center justify-between mb-2" data-id="element-1591">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-1592">
                      Distribuzione utilizzo
                    </span>
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400" data-id="element-1593">
                      {model.usagePercentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-medium rounded-full h-2" data-id="element-1594">
                    <div className="h-2 rounded-full bg-blue-500 dark:bg-blue-600 transition-all" style={{
                width: `${model.usagePercentage}%`
              }} data-id="element-1595" />
                  </div>
                </div>}
            </div>
          </AppCard>)}
      </div>

      {/* Optimization Info */}
      <AppCard className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 border-blue-200 dark:border-blue-800" data-id="element-1596">
        <div className="flex items-start gap-4" data-id="element-1597">
          <div className="p-3 bg-white dark:bg-dark-surface rounded-xl shadow-sm" data-id="element-1598">
            <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-1599" />
          </div>
          <div data-id="element-1600">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-1601">
              Come funziona l'ottimizzazione automatica
            </h3>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-1602">
              <li className="flex items-start gap-2" data-id="element-1603">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-1604" />
                <span data-id="element-1605">
                  <strong data-id="element-1606">Analisi della richiesta:</strong> Il Service valuta la
                  complessità e il tipo di task richiesto
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-1607">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-1608" />
                <span data-id="element-1609">
                  <strong data-id="element-1610">Selezione intelligente:</strong> Sceglie il modello
                  con il miglior rapporto qualità-prezzo per quella specifica
                  richiesta
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-1611">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-1612" />
                <span data-id="element-1613">
                  <strong data-id="element-1614">Bilanciamento del carico:</strong> Distribuisce le
                  richieste tra i modelli per ottimizzare performance e costi
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-1615">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-1616" />
                <span data-id="element-1617">
                  <strong data-id="element-1618">Apprendimento continuo:</strong> Migliora le decisioni
                  nel tempo basandosi sui risultati ottenuti
                </span>
              </li>
            </ul>
          </div>
        </div>
      </AppCard>
    </div>;
}